/* eslint-disable camelcase */
import { useMemo } from 'react';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { QUERY_PARAMS, URL_ROUTES } from '@/constants';
import { type Cart } from '@/modules/cart/domain/Cart';
import type { TPaymentMethodId } from '@/modules/perseo-billing/components/accordion/interfaces';
import type { EditCartHook } from '@/modules/perseo-billing/containers/billing/Summary/hooks';
import { useGetCartTotals } from '@/modules/perseo-billing/hooks/use-cart-payments';
import type { IPaymentRequest } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import type {
  IGTMSendProductsBillingPurchase,
  IGTMSendProductsBillingPurchaseWebView
} from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';
import {
  GtmEvent,
  GtmEventInteractiveAction,
  GtmEventInteractiveCategory,
  GtmEventInteractiveTag,
  GtmPageName,
  GtmPagePath,
  GtmTypeUser
} from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';
import { notifyError } from '@/modules/perseo-billing/services/error-handler';
import { getCurrentCustomer } from '@/modules/perseo-billing/store';
import type { ICustomizableEvent } from '@/modules/perseo-billing/utils';
import {
  getCustomizableEvent,
  getGtmInteractionEvent,
  getGtmPageVirtualEvent
} from '@/modules/perseo-billing/utils';
import {
  isHomeDeliveryOrderConfirmation,
  isIntangibleDeliveryWithoutEmail,
  isNoviosDeliveryWithoutEmail,
  isPickupDelivery
} from '@/modules/perseo-billing/utils/delivery-type-guards';
import type { IErrorHandlerBilling } from '@/modules/perseo-billing/utils/error-handler';
import { createProductObj } from '@/modules/perseo-billing/utils/gtm-objects';
import { cleanProductListBySKU } from '@/modules/perseo-billing/utils/gtm-utils';
import { mergeCustomerData } from '@/modules/perseo-billing/utils/mergeCustomerData';
import { SendBuyNowEvents } from '@/modules/shared/utils/gtmEvents';
import type { EcommerceDataFC } from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import {
  BuyNowEventInteractiveCategories,
  EEventName
} from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import { getItemsEventFC } from '@/modules/shared/utils/gtmEvents/events';
import { sendGtmEvent } from '@/modules/shared/utils/gtmEvents/Manager';
import { sendEventByDevice } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBillingEvents';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import { useFindLocations } from '@/sections/region/hooks/useFindLocations';

const parsePriceDelivery = (value: string | null | undefined): number => {
  if (!value) return 0;

  return (
    Number(
      value.replace(/\$/g, '').replace(/\./g, '').replace(/,/g, '.').trim()
    ) || 0
  );
};

const devices = ['ios', 'android', 'web', 'kiosco'];
type TDevice = (typeof devices)[number];

const CATEGORY_BILLING = GtmEventInteractiveCategory.BillingPayment;
const CATEGORY_ORDER_PROCESSING =
  GtmEventInteractiveCategory.BillingBuyConfirmation;

const getBillingCategory = (): string => {
  const pathname = window.location.pathname;

  const isOrderProcessing =
    pathname.includes(URL_ROUTES.BILLING_ORDER_PROCESSING_SUCCESS_PATH) ||
    pathname.includes(URL_ROUTES.WEDDING_ORDER_PROCESSING_SUCCESS_PATH);

  if (isOrderProcessing) {
    return CATEGORY_ORDER_PROCESSING;
  }

  return CATEGORY_BILLING;
};

const sendGtmBillingEvent = (
  action: string,
  tag: string,
  category?: string
) => {
  const billingCategory = category ?? getBillingCategory();

  sendGtmEvent(
    getGtmInteractionEvent({
      category: billingCategory,
      action: action,
      tag: tag,
      event: GtmEvent.EventInteractive
    })
  );
};

const sendGtmCustomizableEvent = (body: ICustomizableEvent) => {
  sendGtmEvent(getCustomizableEvent(body));
};

const sendGtmBillingCencoPayEvent = (
  category: string,
  hasSavedCards: boolean,
  isAuthenticated: boolean
) => {
  const { BillingRegisteredUser, BillingGuestUser } =
    GtmEventInteractiveTag || {};

  const cencoPayTag = hasSavedCards
    ? 'Con tarjetas guardadas'
    : 'Sin tarjetas guardadas';

  const cencoPayAction = isAuthenticated
    ? BillingRegisteredUser
    : BillingGuestUser;

  sendGtmEvent(
    getGtmInteractionEvent({
      category,
      action: cencoPayAction,
      tag: cencoPayTag,
      event: GtmEvent.EventInteractive,
      isSavedMethod: false
    })
  );
};

export const useGtmBillingProduct = () => {
  const user = getCurrentCustomer();
  const {
    customer: { data: customerData }
  } = useCustomerContext();
  const isGuest = user.isGuestLogin;
  const billing = useAppSelector((state) => state.billing);
  const cart = useAppSelector((state) => state.cart);
  const { otherMethods } = useGetCartTotals();

  const { findCommuneName } = useFindLocations();
  const isGA4ListFeatureEnabled = useAppSelector(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (state) => state?.switches?.perseoGlobal?.isGA4ListFeatureEnabled
  );
  const { selectedPaymentMethods, paymentSuccess, availablePayments } =
    billing || {};

  const { BillingRegisteredUser, BillingGuestUser } =
    GtmEventInteractiveTag || {};

  const { data } = cart || {};

  const hasSavedCards = (availablePayments?.savedCards?.length ?? 0) > 0;

  const totalPrice = otherMethods?.amount ? String(otherMethods?.amount) : '';

  const device: TDevice = useMemo<TDevice>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(QUERY_PARAMS.DEVICE) || 'web';
  }, []);

  const billingPageGtm = (): void => {
    const eventPayload = getGtmPageVirtualEvent({
      page: GtmPagePath.Billing,
      pagename: GtmPageName.Billing
    });

    sendEventByDevice({
      eventName: 'page-virtual',
      payload: eventPayload
    });
  };

  const billingClickPayButtonGtm = (
    payload: IPaymentRequest,
    isSavedCard: boolean
  ): void => {
    sendEventByDevice({
      eventName: 'page-virtual',
      payload: {
        'event-interactive-category': 'Checkout pago',
        'event-interactive-accion': 'Clic pagar',
        'event-interactive-tag': payload?.payments?.[0].paymentProcessor || '',
        'event-interactive-metodoGuardado': isSavedCard,
        'event-interactive-cuotas': payload?.payments?.[0].installments,
        event: 'event-interactive'
      }
    });
  };

  const sendPaymentSelectionGtm = (paymentMethodId: TPaymentMethodId): void => {
    const gtmInteraction = getGtmInteractionEvent({
      category: GtmEventInteractiveCategory.BillingPayment,
      action: GtmEventInteractiveAction.BillingSelectedPaymentMethod,
      tag: paymentMethodId,
      event: GtmEvent.EventInteractive
    });

    sendEventByDevice({
      eventName: GtmEvent.EventInteractive,
      payload: gtmInteraction
    });
  };

  const paymentButtonGtm = (): void => {
    const ids = selectedPaymentMethods?.payments
      ?.map((method) => method?.paymentProcessor)
      .join(' ');
    const isSavedCard = selectedPaymentMethods?.payments?.some((method) => {
      const paymentInfo = method;
      return (
        paymentInfo &&
        'savedCard' in paymentInfo &&
        paymentInfo.savedCard === true
      );
    });

    const gtmInteraction = getGtmInteractionEvent({
      category: GtmEventInteractiveCategory.BillingPayment,
      action: GtmEventInteractiveAction.BillingClickPayment,
      tag: ids ?? '',
      event: GtmEvent.EventInteractive
    });
    gtmInteraction['event-interactive-metodoGuardado'] = isSavedCard;

    sendEventByDevice({
      eventName: 'event-interactive',
      payload: gtmInteraction
    });
  };

  const addCouponGtm = (coupon: string): void => {
    const { CouponApply } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(CouponApply, coupon);
  };

  const clickDeleteCouponGtm = (coupon: string): void => {
    const { BillingClickRemoveCoupon } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingClickRemoveCoupon, coupon);
  };

  const deleteCouponGtm = (coupon: string): void => {
    const { BillingRemoveCouponConfirm } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingRemoveCouponConfirm, coupon);
  };

  const errorOnCoupon = (coupon: string): void => {
    const { CouponApplyError } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(CouponApplyError, coupon);
  };

  const invalidCouponGtm = (coupon: string): void => {
    const { BillingErrorRestrictionsCoupon } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingErrorRestrictionsCoupon, coupon);
  };

  const cancelDeleteCouponGtm = (coupon: string): void => {
    const { BillingRemoveCouponCancel } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingRemoveCouponCancel, coupon);
  };

  const clickLoginOrRegister = (): void => {
    const { BillingLoginOrRegister } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingLoginOrRegister, totalPrice);
  };

  const goToCartGtm = (context?: EditCartHook): void => {
    const { BillingLinkToCart, BillingLinkToMultishipping } =
      GtmEventInteractiveAction || {};
    const action =
      context === 'summary' ? BillingLinkToCart : BillingLinkToMultishipping;
    const body = {
      category: GtmEventInteractiveCategory.BillingPayment,
      action,
      tag: totalPrice,
      event: GtmEvent.EventInteractive
    };
    sendGtmCustomizableEvent(body);
  };

  const sendSaveCardGtm = (isCencosud?: boolean): void => {
    const tag = isCencosud ? 'Tarjeta Cencosud' : 'Tarjeta Crédito';
    sendGtmCustomizableEvent({
      category: GtmEventInteractiveCategory.BillingPayment,
      action: GtmEventInteractiveAction.BillingAcceptSaveCard,
      tag
    });
  };

  const goToCheckoutMultishippingGtm = (): void => {
    const { BillingLinkToMultishipping } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingLinkToMultishipping, totalPrice);
  };

  const linkNeedInvoiceGtm = (): void => {
    const { BillingLinkNeedInvoice } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingLinkNeedInvoice, totalPrice);
  };

  const saveInvoiceDataGtm = (): void => {
    const { BillingSaveInvoiceData } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingSaveInvoiceData, totalPrice);
  };

  const editCompanyGtm = (): void => {
    const { BillingEditCompany } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingEditCompany, totalPrice);
  };

  const deleteCompanyButtonGtm = (): void => {
    const { BillingDeleteCompanyButton } = GtmEventInteractiveAction || {};
    const { BillingClickIconX } = GtmEventInteractiveTag || {};
    sendGtmBillingEvent(BillingDeleteCompanyButton, BillingClickIconX);
  };

  const successfullyDeletedCompanyGtm = (): void => {
    const { BillingDeletedCompanySuccess } = GtmEventInteractiveAction || {};
    const nameOfCompany = cart?.data?.billingAddress?.companyName || '';
    sendGtmBillingEvent(
      BillingDeletedCompanySuccess,
      nameOfCompany,
      GtmEventInteractiveCategory.BillingInvoiceModal
    );
  };

  const deleteCompanyGoBackGtm = (): void => {
    const { BillingDeleteCompanyGoBack } = GtmEventInteractiveAction || {};
    const nameOfCompany = cart?.data?.billingAddress?.companyName || '';
    sendGtmBillingEvent(
      BillingDeleteCompanyGoBack,
      nameOfCompany,
      GtmEventInteractiveCategory.BillingInvoiceModal
    );
  };

  const continueWithInvoiceGtm = (tag: string): void => {
    const { BillingContinueWithInvoice } = GtmEventInteractiveAction || {};
    const { BillingPayWithInvoiceModal } = GtmEventInteractiveCategory || {};
    sendGtmBillingEvent(
      BillingContinueWithInvoice,
      tag,
      BillingPayWithInvoiceModal
    );
  };

  const continueWithReceiptGtm = (): void => {
    const { BillingContinueWithReceipt } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingContinueWithReceipt, totalPrice);
  };

  const saveCardPaymentMethod = (): void => {
    const ids =
      selectedPaymentMethods?.payments
        ?.map((method) => method?.paymentProcessor)
        .join(' ') ?? '';
    const isSavedCard = selectedPaymentMethods?.payments?.some((method) => {
      const paymentInfo = method;
      return (
        paymentInfo &&
        'savedCard' in paymentInfo &&
        paymentInfo.savedCard === true
      );
    });
    if (isSavedCard) {
      const { BillingSaveCard } = GtmEventInteractiveAction || {};
      sendGtmBillingEvent(BillingSaveCard, ids);
    }
  };

  const addGiftCardGtm = (cardNumber: string): void => {
    const { BillingAddGiftCard } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingAddGiftCard, cardNumber);
  };

  const linkGiftCardFindItGtm = (): void => {
    const { BillingGiftCardInteraction } = GtmEventInteractiveAction || {};
    const { BillingGiftCardFindIt } = GtmEventInteractiveTag || {};
    sendGtmBillingEvent(BillingGiftCardInteraction, BillingGiftCardFindIt);
  };

  const linkOkGiftCardFindItGtm = (): void => {
    const { BillingGiftCardInteraction } = GtmEventInteractiveAction || {};
    const { BillingGiftCardFindItOk } = GtmEventInteractiveTag || {};
    sendGtmBillingEvent(BillingGiftCardInteraction, BillingGiftCardFindItOk);
  };

  const informativeMessageGiftCard = (giftCardMessage: string): void => {
    const { BillingGiftCardInfoMessage } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingGiftCardInfoMessage, giftCardMessage);
  };

  const linkRemoveGiftCardGtm = (): void => {
    const { BillingGiftCardInteraction } = GtmEventInteractiveAction || {};
    const { BillingRemoveGiftCard } = GtmEventInteractiveTag || {};
    sendGtmBillingEvent(BillingGiftCardInteraction, BillingRemoveGiftCard);
  };

  const addExtraPaymentToGiftCardConfirm = (): void => {
    const { BillingGiftCardInteraction } = GtmEventInteractiveAction || {};
    const { BillingSelectOtherPaymentMethodWithGiftCard } =
      GtmEventInteractiveTag || {};

    sendGtmBillingEvent(
      BillingGiftCardInteraction,
      BillingSelectOtherPaymentMethodWithGiftCard
    );
  };

  const orderConfirmationPageGtm = (): void => {
    const eventPayload = getGtmPageVirtualEvent({
      page: GtmPagePath.CheckoutConfirmation,
      pagename: GtmPageName.CheckoutConfirmation
    });

    sendEventByDevice({
      eventName: 'page-virtual',
      payload: eventPayload
    });
  };

  const sendProductsFromOrderConfirmationGtm = (): void => {
    const isFastCheckout = billing?.paymentSuccess?.isFastCheckout;
    const products = data?.lineItems || [];
    if (
      !paymentSuccess?.basketSummary ||
      paymentSuccess?.basketSummary?.length === 0
    ) {
      return;
    }
    const isWebView = device !== 'web';

    const { totalSummary, order, isCencoPay } = paymentSuccess;
    const customer = mergeCustomerData(paymentSuccess?.customer, customerData);

    const summary = totalSummary?.summary;
    const revenue = summary?.unformattedTotalPrice;
    const paymentMethod = isCencoPay
      ? totalSummary?.paymentMethod?.[0]?.bankCardType?.toLowerCase()
      : totalSummary?.paymentMethod?.[0]?.type;
    const isdCencoPrice = !!summary?.isCencoPrice;
    const productsObj = createProductObj(products, isWebView, isdCencoPrice);

    const typeUser: GtmTypeUser = cart?.data?.customerId
      ? GtmTypeUser.Registered
      : GtmTypeUser.Guest;

    const eventPayload: IGTMSendProductsBillingPurchase = {
      event: 'checkout',
      'type-User': typeUser,
      'payment-method': paymentMethod ?? '',
      ecommerce: {
        purchase: {
          actionField: {
            action: 'checkout',
            affiliation: 'shop',
            coupon: paymentSuccess?.gtm?.coupon ?? '',
            currency: summary?.currency ?? 'CLP',
            id: order || '',
            option: 'purchase',
            revenue: revenue ?? 0,
            shipping: parsePriceDelivery(summary?.totalDeliveryPrice),
            step: '4',
            tax: summary?.totalTax ?? 0
          },
          products: productsObj,
          documentType:
            customer?.documentType ??
            cart?.data?.shippingAddress?.documentType ??
            'RUT'
        }
      }
    };

    const addExtraParamsForWebview = () => {
      const { discountCodes } = cart?.data || {};

      const discountCode = discountCodes?.[0]?.discountCode?.obj?.code || '';

      const webViewEventPayload =
        eventPayload as IGTMSendProductsBillingPurchaseWebView;
      const payloadExtraForWebview = {
        AnalyticsParameterAffiliation: 'shop', // static value 'shop',
        AnalyticsParameterCurrency: totalSummary?.summary?.currency ?? 'CLP', // Site.getCurrent().getDefaultCurrency(),
        AnalyticsParameterValue:
          totalSummary?.summary?.unformattedTotalPrice ?? 0, // orderTotal.value,
        AnalyticsParameterCoupon: discountCode || '',
        AnalyticsParameterShippingTier: null, // order.defaultShipment.shippingMethod.displayName,
        AnalyticsParameterPaymentType:
          paymentSuccess?.totalSummary?.paymentMethod?.[0]?.type === 'GIFT_CARD'
            ? 'GIFTCARD'
            : (paymentSuccess?.totalSummary?.paymentMethod?.[0]?.bankCardType ??
              ''), // * this is `paymentTypeCode`
        AnalyticsParameterShipping:
          Number(totalSummary?.summary?.totalDeliveryPrice) ?? 0, //  shippingCost.value,
        AnalyticsParameterTax: totalSummary?.summary?.totalTax ?? 0 // order.totalTax.value
      };

      webViewEventPayload.ecommerce['purchaseParams'] = payloadExtraForWebview;

      webViewEventPayload.ecommerce['checkout'] = {
        checkoutParams: payloadExtraForWebview
      };

      if (webViewEventPayload.ecommerce.purchase) {
        webViewEventPayload.ecommerce.purchase.purchaseParams =
          payloadExtraForWebview;
      } else {
        webViewEventPayload.ecommerce.purchase = {
          ...eventPayload.ecommerce.purchase,
          purchaseParams: payloadExtraForWebview
        };
      }
    };

    // Check user device and send event accordingly
    if (device !== 'web') {
      addExtraParamsForWebview();
    }

    if (device === 'web' && isGA4ListFeatureEnabled) {
      try {
        productsObj.forEach((product) => {
          if ('id' in product) {
            cleanProductListBySKU(product.id);
          }
        });
      } catch (error) {
        notifyError(error);
      }
    }

    if (isFastCheckout) {
      const eventPayloadFC: EcommerceDataFC = {
        flujo_checkout: BuyNowEventInteractiveCategories.FastCheckout,
        payment_method: paymentMethod ?? '',
        transaction_id: order || '',
        value: revenue ?? 0,
        shipping: parsePriceDelivery(summary?.totalDeliveryPrice),
        tax: summary?.totalTax ?? 0,
        coupon: paymentSuccess?.gtm?.coupon ?? '',
        affiliation: 'shop',
        currency: summary?.currency ?? 'CLP',
        items: billing?.paymentSuccess?.basketSummary
          ? getItemsEventFC(
              cart?.data ?? ({} as Cart),
              billing?.paymentSuccess?.basketSummary
            )
          : []
      };

      SendBuyNowEvents.sendEvent({
        event: EEventName.PURCHASE,
        extraFields: {
          ecommerce: eventPayloadFC
        }
      });
    } else {
      sendEventByDevice({
        eventName: 'checkoutPurchaseConfirmation',
        payload: eventPayload
      });
    }
  };

  const sendCheckoutUserGtm = (): void => {
    const customer = paymentSuccess?.customer;
    if (!customer) return;
    const { firstName, lastName, email, phone, city, postCode, country } =
      customer;

    const basketSummary =
      paymentSuccess?.basketSummary?.[0]?.deliveryDetails || {};

    let streetAddress = '';
    let region = '';
    let communeName = '';

    if (isHomeDeliveryOrderConfirmation(basketSummary)) {
      const {
        streetName,
        streetNumber,
        region: deliveryRegion,
        commune
      } = basketSummary;
      streetAddress = `${streetName || ''} ${streetNumber || ''}`.trim();
      region = deliveryRegion;
      communeName = findCommuneName(Number(deliveryRegion), commune);
    } else if (isPickupDelivery(basketSummary)) {
      const {
        pickupStoreStreetName,
        pickupStoreStreetNumber,
        pickupStoreCommune
      } = basketSummary;
      streetAddress =
        `${pickupStoreStreetName || ''} ${pickupStoreStreetNumber || ''}`.trim();
      communeName = pickupStoreCommune || '';
    } else if (isIntangibleDeliveryWithoutEmail(basketSummary)) {
      streetAddress = '';
    } else if (isNoviosDeliveryWithoutEmail(basketSummary)) {
      streetAddress = '';
    }

    const eventPayload = {
      email,
      phone_number: phone,
      first_name: firstName,
      last_name: lastName,
      street_address: streetAddress,
      city,
      region,
      postCode: postCode ?? '',
      country,
      commune: communeName,
      event: GtmEvent.EventUserIdentification
    };

    sendGtmEvent(eventPayload);
  };

  const sendPaymentErrorGtm = (error: IErrorHandlerBilling): void => {
    const eventPayload = getGtmInteractionEvent({
      category: GtmEventInteractiveCategory.BillingPayment,
      action: GtmEventInteractiveAction.BillingPaymentErrorResponse,
      tag: error?.errorCode ?? '',
      event: GtmEvent.EventInteractive
    });

    sendEventByDevice({
      eventName: 'page-virtual',
      payload: eventPayload
    });
  };

  const sendClubParisGtm = (code: string): void => {
    const { BillingClubParis } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingClubParis, code);
  };

  const sendClubParisRemoveIntentGtm = (code: string): void => {
    const { BillingClubParisRemoveIntent } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingClubParisRemoveIntent, code);
  };

  const sendClubParisRemovedGtm = (code: string): void => {
    const { BillingClubParisRemoved } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingClubParisRemoved, code);
  };

  const clubParisErrorGtm = (error: string): void => {
    const { BillingClubParisError } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingClubParisError, error);
  };

  const continueWithWebpayGtm = (): void => {
    const { BillingClickPayment } = GtmEventInteractiveAction || {};
    const { BillingContinueWithWebpay } = GtmEventInteractiveTag || {};

    const gtmInteraction = getGtmInteractionEvent({
      category: getBillingCategory(),
      action: BillingClickPayment,
      tag: BillingContinueWithWebpay,
      event: GtmEvent.EventInteractive
    });

    gtmInteraction['event-interactive-metodoGuardado'] = hasSavedCards;

    sendEventByDevice({
      eventName: 'event-interactive',
      payload: gtmInteraction
    });
  };

  const showMoreOrLessLinkGtm = (option: 'more' | 'less'): void => {
    const { SavedCards } = GtmEventInteractiveAction || {};
    const { BillingShowMoreClick, BillingShowLessClick } =
      GtmEventInteractiveTag || {};

    const isShowMore = option === 'more';
    const tag = isShowMore ? BillingShowMoreClick : BillingShowLessClick;

    sendGtmBillingEvent(SavedCards, tag);
  };

  const expiredCardGtm = (expiredPaymentMethod: string): void => {
    const { BillingExpiredCard } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingExpiredCard, expiredPaymentMethod);
  };

  // * CENCOPAY

  //* 1 y 2
  const cencoPaySavedCardsGtm = (): void => {
    const category = GtmEventInteractiveCategory.BillingBuyCencoPay;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  //* 3
  const cencoPayCloseModalGtm = (): void => {
    const category = GtmEventInteractiveCategory.BillingCloseCencoPayModal;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  //* 4
  const cencoPaySelectOtherPaymentMethodGtm = (): void => {
    const category =
      GtmEventInteractiveCategory.BillingSelectOtherPaymentMethod;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  //* 5
  const cencoPayGenerateNewCode = (): void => {
    const category = GtmEventInteractiveCategory.BillingGenerateNewCode;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  //* 6
  const cencoPayExpiredQR = (): void => {
    const category = GtmEventInteractiveCategory.BillingExpiredQR;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  //* 7
  const cencoPayConnectError = (errorCode?: string): void => {
    const category = GtmEventInteractiveCategory.BillingcencoPayConnectError;
    const cencoPayTag = hasSavedCards
      ? 'Tarjetas guardadas'
      : 'Sin tarjetas guardadas';

    sendGtmEvent(
      getGtmInteractionEvent({
        category: category,
        action: errorCode || '',
        tag: cencoPayTag,
        event: GtmEvent.EventInteractive
      })
    );
  };

  //*8
  const cencoPayTryOtherPayment = (): void => {
    const category = GtmEventInteractiveCategory.BillingCencoPayModalChange;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  //* 9
  const cencoPayModalGoBack = (): void => {
    const category = GtmEventInteractiveCategory.BillingcencoPayModalGoBack;
    sendGtmBillingCencoPayEvent(category, hasSavedCards, !isGuest);
  };

  // * ORDER CONFIRMATION

  const goToMyPurchasesGtm = (): void => {
    const { BillingMyPurchasesButton } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingMyPurchasesButton, BillingRegisteredUser);
  };

  const contactFormButtonGtm = (): void => {
    const { BillingContactForm } = GtmEventInteractiveAction || {};
    const tag = !isGuest ? BillingRegisteredUser : BillingGuestUser;
    sendGtmBillingEvent(BillingContactForm, tag);
  };

  const requestCancelationGtm = (): void => {
    const { BillingRequestCancelation } = GtmEventInteractiveAction || {};
    const tag = !isGuest ? BillingRegisteredUser : BillingGuestUser;
    sendGtmBillingEvent(BillingRequestCancelation, tag);
  };

  const contactByWhatsappGtm = (): void => {
    const tag = !isGuest ? BillingRegisteredUser : BillingGuestUser;
    const { BillingContactByWhatsapp } = GtmEventInteractiveAction || {};
    sendGtmBillingEvent(BillingContactByWhatsapp, tag);
  };

  return {
    billingPageGtm,
    billingClickPayButtonGtm,
    paymentButtonGtm,
    sendPaymentSelectionGtm,
    addCouponGtm,
    clickDeleteCouponGtm,
    deleteCouponGtm,
    cancelDeleteCouponGtm,
    errorOnCoupon,
    clickLoginOrRegister,
    goToCartGtm,
    goToCheckoutMultishippingGtm,
    linkNeedInvoiceGtm,
    linkGiftCardFindItGtm,
    saveCardPaymentMethod,
    addGiftCardGtm,
    linkOkGiftCardFindItGtm,
    informativeMessageGiftCard,
    linkRemoveGiftCardGtm,
    saveInvoiceDataGtm,
    editCompanyGtm,
    deleteCompanyButtonGtm,
    successfullyDeletedCompanyGtm,
    deleteCompanyGoBackGtm,
    continueWithInvoiceGtm,
    continueWithReceiptGtm,
    orderConfirmationPageGtm,
    sendProductsFromOrderConfirmationGtm,
    sendCheckoutUserGtm,
    sendPaymentErrorGtm,
    sendClubParisGtm,
    sendClubParisRemoveIntentGtm,
    sendClubParisRemovedGtm,
    sendSaveCardGtm,
    continueWithWebpayGtm,
    showMoreOrLessLinkGtm,
    expiredCardGtm,
    cencoPaySavedCardsGtm,
    cencoPayCloseModalGtm,
    cencoPaySelectOtherPaymentMethodGtm,
    cencoPayGenerateNewCode,
    cencoPayExpiredQR,
    cencoPayConnectError,
    cencoPayTryOtherPayment,
    cencoPayModalGoBack,
    goToMyPurchasesGtm,
    requestCancelationGtm,
    contactByWhatsappGtm,
    contactFormButtonGtm,
    clubParisErrorGtm,
    addExtraPaymentToGiftCardConfirm,
    invalidCouponGtm
  };
};
